import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useToast } from '../hooks/use-toast';
import { Truck, Zap, Users, Droplet, Factory, FlaskConical, Mail, Phone, ArrowRight, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const { toast } = useToast();
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referenceProjects, setReferenceProjects] = useState([]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    fetch(`${API_URL}/api/projects`)
      .then(res => res.json())
      .then(data => setReferenceProjects(data))
      .catch(() => setReferenceProjects([]));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Submit failed');
      toast({
        title: t(language, 'contact.form.success'),
        description: t(language, 'contact.form.successDesc'),
      });
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      toast({
        title: t(language, 'contact.form.error'),
        description: t(language, 'contact.form.errorDesc'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getImageSrc = (image) => {
    if (!image) return '';
    if (image.startsWith('http')) return image;
    return `${API_URL}${image}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header scrollToSection={scrollToSection} />
      
      {/* Hero Section with Video */}
      <section className="relative h-screen min-h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover">
            <source src="https://videos.pexels.com/video-files/3129957/3129957-uhd_2560_1440_25fps.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/70 to-slate-900/90"></div>
        </div>
        
        <div className="relative z-10 h-full flex items-center px-6">
          <div className="max-w-7xl mx-auto w-full">
            <div className="max-w-3xl">
              <h1 data-testid="hero-title" className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                {t(language, 'hero.title')}
                <span className="block text-emerald-400 mt-3">{t(language, 'hero.subtitle')}</span>
              </h1>
              <p className="text-xl md:text-2xl text-slate-200 mb-10 leading-relaxed">
                {t(language, 'hero.description')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  data-testid="hero-quote-btn"
                  size="lg"
                  onClick={() => scrollToSection('contact')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105 shadow-2xl">
                  {t(language, 'hero.requestQuote')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  data-testid="hero-services-btn"
                  size="lg"
                  variant="outline"
                  onClick={() => scrollToSection('services')}
                  className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg transition-all duration-300 backdrop-blur-sm">
                  {t(language, 'hero.ourServices')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div className="p-6">
              <div className="text-4xl font-bold text-emerald-600 mb-2">15+</div>
              <div className="text-slate-600 font-medium">{t(language, 'trust.experience')}</div>
            </div>
            <div className="p-6">
              <div className="text-lg font-bold text-emerald-600 mb-2">{t(language, 'trust.endToEnd')}</div>
              <div className="text-slate-600 text-sm leading-relaxed">{t(language, 'trust.endToEndDesc')}</div>
            </div>
            <div className="p-6">
              <div className="text-lg font-bold text-emerald-600 mb-2">{t(language, 'trust.knowHow')}</div>
              <div className="text-slate-600 text-sm leading-relaxed">{t(language, 'trust.knowHowDesc')}</div>
            </div>
            <div className="p-6">
              <div className="text-4xl font-bold text-emerald-600 mb-2">100%</div>
              <div className="text-slate-600 font-medium">{t(language, 'trust.quality')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">{t(language, 'about.title')}</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t(language, 'about.description')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 hover:border-emerald-500 transition-all duration-300 hover:shadow-xl">
              <CardHeader className="pb-4">
                <div className="w-20 h-20 rounded-full overflow-hidden mb-4 bg-slate-300">
                  <img src="https://customer-assets.emergentagent.com/job_48d80cb1-fc51-4209-83f0-3e3a598df3f8/artifacts/nbikq4zg_IgorStojiljkovic_profilna.jpg" alt="Igor Stojiljković" className="w-full h-full object-cover brightness-90" />
                </div>
                <CardTitle className="text-2xl">{t(language, 'about.igor.name')}</CardTitle>
                <CardDescription className="text-lg">{t(language, 'about.igor.title')}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed mb-4 whitespace-pre-line">
                  {t(language, 'about.igor.description')}
                </p>
                <div className="flex items-center gap-2 text-emerald-600">
                  <Mail className="h-4 w-4" />
                  <a href="mailto:istojiljkovic@noble-gs.com" className="hover:underline text-sm">
                    istojiljkovic@noble-gs.com
                  </a>
                </div>
                <div className="flex items-center gap-2 text-emerald-600 mt-2">
                  <Phone className="h-4 w-4" />
                  <a href="tel:+381611340590" className="hover:underline text-sm">
                    +381 61 13 40 590 (WhatsApp, Viber)
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-emerald-500 transition-all duration-300 hover:shadow-xl">
              <CardHeader className="pb-4">
                <div className="w-20 h-20 rounded-full overflow-hidden mb-4">
                  <img src="https://customer-assets.emergentagent.com/job_48d80cb1-fc51-4209-83f0-3e3a598df3f8/artifacts/hcmsh5pe_BranislavPilipovic_Gmail_250x250.png" alt="Branislav Pilipović" className="w-full h-full object-cover" />
                </div>
                <CardTitle className="text-2xl">{t(language, 'about.branislav.name')}</CardTitle>
                <CardDescription className="text-lg">{t(language, 'about.branislav.title')}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed mb-4 whitespace-pre-line">
                  {t(language, 'about.branislav.description')}
                </p>
                <div className="flex items-center gap-2 text-emerald-600">
                  <Mail className="h-4 w-4" />
                  <a href="mailto:bpilipovic@noble-gs.com" className="hover:underline text-sm">
                    bpilipovic@noble-gs.com
                  </a>
                </div>
                <div className="flex items-center gap-2 text-emerald-600 mt-2">
                  <Phone className="h-4 w-4" />
                  <a href="tel:+381652426710" className="hover:underline text-sm">
                    +381 65 24 26 710 (WhatsApp, Viber)
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">{t(language, 'services.title')}</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t(language, 'services.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="border-2 hover:border-emerald-500 transition-all duration-300 hover:shadow-xl group">
              <CardHeader>
                <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors duration-300">
                  <Truck className="h-8 w-8 text-emerald-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <CardTitle className="text-xl">{t(language, 'services.logistics.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-slate-600">
                  {t(language, 'services.logistics.features').map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-emerald-500 transition-all duration-300 hover:shadow-xl group">
              <CardHeader>
                <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors duration-300">
                  <Factory className="h-8 w-8 text-emerald-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <CardTitle className="text-xl">{t(language, 'services.engineering.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-slate-600">
                  {t(language, 'services.engineering.features').map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-emerald-500 transition-all duration-300 hover:shadow-xl group">
              <CardHeader>
                <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors duration-300">
                  <Users className="h-8 w-8 text-emerald-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <CardTitle className="text-xl">{t(language, 'services.advisory.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-slate-600">
                  {t(language, 'services.advisory.features').map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">{t(language, 'products.title')}</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t(language, 'products.description')}
            </p>
          </div>

          <div className="grid lg:grid-cols-1 gap-8 max-w-6xl mx-auto">
            {/* Liquid Argon 5.0 */}
            <Card className="border-2 border-emerald-500 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-emerald-50 border-b">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-600 rounded-lg flex items-center justify-center">
                    <Droplet className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">{t(language, 'products.argon.title')}</CardTitle>
                    <CardDescription className="text-lg">{t(language, 'products.argon.subtitle')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.keyFeatures')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.argon.features').map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.applications')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.argon.apps').map((app, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <FlaskConical className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{app}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* DME */}
            <Card className="border-2 border-emerald-500 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-emerald-50 border-b">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-600 rounded-lg flex items-center justify-center">
                    <Zap className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">{t(language, 'products.dme.title')}</CardTitle>
                    <CardDescription className="text-lg">{t(language, 'products.dme.subtitle')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.keyFeatures')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.dme.features').map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.applications')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.dme.apps').map((app, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <FlaskConical className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{app}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Helium 5.0 */}
            <Card className="border-2 border-emerald-500 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-emerald-50 border-b">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-600 rounded-lg flex items-center justify-center">
                    <Factory className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">{t(language, 'products.helium.title')}</CardTitle>
                    <CardDescription className="text-lg">{t(language, 'products.helium.subtitle')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.keyFeatures')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.helium.features').map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.applications')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.helium.apps').map((app, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <FlaskConical className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{app}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Liquid Nitrogen */}
            <Card className="border-2 border-emerald-500 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-emerald-50 border-b">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-600 rounded-lg flex items-center justify-center">
                    <Droplet className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">{t(language, 'products.nitrogen.title')}</CardTitle>
                    <CardDescription className="text-lg">{t(language, 'products.nitrogen.subtitle')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.keyFeatures')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.nitrogen.features').map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.applications')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.nitrogen.apps').map((app, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <FlaskConical className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{app}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Liquid Oxygen */}
            <Card className="border-2 border-emerald-500 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-emerald-50 border-b">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-600 rounded-lg flex items-center justify-center">
                    <Droplet className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">{t(language, 'products.oxygen.title')}</CardTitle>
                    <CardDescription className="text-lg">{t(language, 'products.oxygen.subtitle')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.keyFeatures')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.oxygen.features').map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.applications')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.oxygen.apps').map((app, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <FlaskConical className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{app}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Refrigerant Gases */}
            <Card className="border-2 border-emerald-500 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-emerald-50 border-b">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-600 rounded-lg flex items-center justify-center">
                    <Zap className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">{t(language, 'products.refrigerants.title')}</CardTitle>
                    <CardDescription className="text-lg">{t(language, 'products.refrigerants.subtitle')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.keyFeatures')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.refrigerants.features').map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.applications')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.refrigerants.apps').map((app, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <FlaskConical className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{app}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Calcium Carbide */}
            <Card className="border-2 border-emerald-500 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-emerald-50 border-b">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-600 rounded-lg flex items-center justify-center">
                    <FlaskConical className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">{t(language, 'products.carbide.title')}</CardTitle>
                    <CardDescription className="text-lg">{t(language, 'products.carbide.subtitle')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.keyFeatures')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.carbide.features').map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.applications')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.carbide.apps').map((app, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <FlaskConical className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{app}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Molecular Sieves */}
            <Card className="border-2 border-emerald-500 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-emerald-50 border-b">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-600 rounded-lg flex items-center justify-center">
                    <Factory className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">{t(language, 'products.sieves.title')}</CardTitle>
                    <CardDescription className="text-lg">{t(language, 'products.sieves.subtitle')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.keyFeatures')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.sieves.features').map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 text-lg">{t(language, 'products.argon.applications')}</h4>
                    <ul className="space-y-3 text-slate-600">
                      {t(language, 'products.sieves.apps').map((app, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <FlaskConical className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{app}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">{t(language, 'products.moreInfo')}</p>
            <Button
              onClick={() => scrollToSection('contact')}
              variant="outline"
              className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
              {t(language, 'products.contactButton')}
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">{t(language, 'faq.title')}</h2>
            <p className="text-xl text-slate-600">{t(language, 'faq.description')}</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-2" className="border-2 border-slate-200 rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold hover:text-emerald-600">
                {t(language, 'faq.q2.question')}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                {t(language, 'faq.q2.answer')}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border-2 border-slate-200 rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold hover:text-emerald-600">
                {t(language, 'faq.q3.question')}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                {t(language, 'faq.q3.answer')}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border-2 border-slate-200 rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold hover:text-emerald-600">
                {t(language, 'faq.q4.question')}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                {t(language, 'faq.q4.answer')}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Reference Projects Section */}
      <section id="projects" className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">{t(language, 'projects.title')}</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t(language, 'projects.description')}
            </p>
          </div>

          {referenceProjects.length === 0 ? (
            <div className="text-center py-12" data-testid="no-projects-home">
              <p className="text-slate-500 text-lg">{t(language, 'projects.noProjects')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="projects-section-grid">
              {referenceProjects.map((project) => (
                <Card key={project.id} className="border-2 hover:border-emerald-500 transition-all duration-300 hover:shadow-xl overflow-hidden group">
                  <div className="relative h-64 overflow-hidden bg-slate-200">
                    {project.image ? (
                      <img
                        src={getImageSrc(project.image)}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">No Image</div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{project.title}</CardTitle>
                    <CardDescription className="text-sm text-slate-500">
                      {project.location} - {project.year}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 leading-relaxed mb-4">{project.description}</p>
                    {project.tags && project.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {project.tags.map((tag, index) => (
                          <span key={index} className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">{t(language, 'projects.interested')}</p>
            <Button
              data-testid="projects-contact-btn"
              onClick={() => scrollToSection('contact')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8">
              {t(language, 'projects.contactUs')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t(language, 'contact.title')}</h2>
            <p className="text-xl text-slate-300">{t(language, 'contact.description')}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h3 className="text-2xl font-semibold mb-6">{t(language, 'contact.info')}</h3>
              
              <div className="space-y-6">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Mail className="h-5 w-5 text-emerald-400" />
                      {t(language, 'contact.email')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <a href="mailto:office@noble-gs.com" className="block text-emerald-400 hover:text-emerald-300 transition-colors">
                      office@noble-gs.com
                    </a>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Phone className="h-5 w-5 text-emerald-400" />
                      {t(language, 'contact.phone')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <a href="tel:+381652426710" className="block text-emerald-400 hover:text-emerald-300 transition-colors">
                      +381 65 24 26 710 (WhatsApp, Viber)
                    </a>
                    <a href="tel:+381611340590" className="block text-emerald-400 hover:text-emerald-300 transition-colors">
                      +381 61 13 40 590 (WhatsApp, Viber)
                    </a>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-6">{t(language, 'contact.sendMessage')}</h3>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="text-white">{t(language, 'contact.form.name')} *</Label>
                  <Input
                    data-testid="contact-name-input"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
                    placeholder={t(language, 'contact.form.namePlaceholder')}
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="text-white">{t(language, 'contact.form.email')} *</Label>
                  <Input
                    data-testid="contact-email-input"
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
                    placeholder={t(language, 'contact.form.emailPlaceholder')}
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone" className="text-white">{t(language, 'contact.form.phone')}</Label>
                  <Input
                    data-testid="contact-phone-input"
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
                    placeholder={t(language, 'contact.form.phonePlaceholder')}
                  />
                </div>
                
                <div>
                  <Label htmlFor="company" className="text-white">{t(language, 'contact.form.company')}</Label>
                  <Input
                    data-testid="contact-company-input"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
                    placeholder={t(language, 'contact.form.companyPlaceholder')}
                  />
                </div>
                
                <div>
                  <Label htmlFor="message" className="text-white">{t(language, 'contact.form.message')} *</Label>
                  <Textarea
                    data-testid="contact-message-input"
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500 resize-none"
                    placeholder={t(language, 'contact.form.messagePlaceholder')}
                  />
                </div>
                
                <Button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg transition-all duration-300 hover:scale-105">
                  {isSubmitting ? t(language, 'contact.form.sending') : t(language, 'contact.form.send')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
